// =====================
// LocalStorage Key Constants
// =====================

export const STORAGE_KEYS = {
  // Authentication tokens for organization users
  ID_TOKEN: 'idToken',
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',

  // Session management
  HAS_LOGGED_OUT: 'hasLoggedOut',

  // Onboarding status cache (keyed by orgId)
  ONBOARDING_COMPLETED: 'onboardingCompleted',

  // Set when onboarding finishes, or by "Show me around" (sidebar footer, Settings, or the help menu);
  // the first-run tour opens on the next dashboard visit and clears it (keyed by orgId, RUT-3782)
  FIRST_RUN_TOUR_PENDING: 'firstRunTourPending',

  // Platform-operator "view as" session banner details (RUT-3105). Presence also drives the banner.
  VIEW_AS: 'viewAs',
} as const;

// Token-related event names
export const AUTH_EVENTS = {
  TOKENS_UPDATED: 'tokensUpdated',
} as const;

// =====================
// Ruta Website URL
// =====================

export function getRutaWebsiteUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const match = apiUrl.match(/https?:\/\/([^.]+)\.api\.getruta\.com/);
  if (match && ['dev', 'staging'].includes(match[1])) {
    return `https://${match[1]}.getruta.com`;
  }
  return 'https://getruta.com';
}

// =====================
// Static assets (R2 behind Cloudflare)
// =====================

/**
 * Where media the app ships with — the onboarding clip, say — is served from: the R2
 * assets bucket on its per-environment host (`api:cloudflareAssetsDomain` in each
 * Pulumi stack). Derived from VITE_API_URL like the portal host below:
 *   - prod:    assets.getruta.com
 *   - dev:     dev.assets.getruta.com
 *   - staging: staging.assets.getruta.com
 * A local build talks to dev data, so it reads dev assets too.
 */
export function getAssetUrl(key: string): string {
  const prefix = apiEnvPrefix();
  // No match at all is a local build, which talks to dev.
  const envPrefix = prefix === null ? 'dev' : prefix;
  const host = envPrefix ? `${envPrefix}.assets.getruta.com` : 'assets.getruta.com';
  return `https://${host}/${key.replace(/^\/+/, '')}`;
}

/**
 * The environment prefix of the API this build talks to: 'dev' or 'staging' from
 * `https://dev.api.getruta.com`, undefined for production's `https://api.getruta.com`,
 * null when VITE_API_URL is not a getruta.com API at all (localhost).
 */
function apiEnvPrefix(): string | undefined | null {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const match = apiUrl.match(/https?:\/\/(?:([^.]+)\.)?api\.getruta\.com/);
  return match ? match[1] : null;
}

// =====================
// Customer Portal URL Generation
// =====================

/**
 * Get the full customer portal URL including origin.
 * Used by admin to generate shareable links for customers.
 *
 * Portal uses subdomain-based routing:
 *   - prod:    {slug}.getruta.com
 *   - dev:     dev.{slug}.getruta.com
 *   - staging: staging.{slug}.getruta.com
 *
 * @param slug - Organization slug
 * @param path - Optional path (e.g., 'session')
 */
export function getFullCustomerPortalUrl(
  slug: string,
  path: string = ''
): string {
  const pathSuffix = path ? `/${path}` : '';

  // Extract environment prefix from API URL to build subdomain-based portal URL
  // e.g., https://dev.api.getruta.com -> dev.{slug}.getruta.com
  const envPrefix = apiEnvPrefix(); // 'dev', 'staging', or undefined for prod
  if (envPrefix !== null) {
    const domain = envPrefix ? `${envPrefix}.${slug}.getruta.com` : `${slug}.getruta.com`;
    return `https://${domain}${pathSuffix}`;
  }

  // Fallback for localhost: use path-based routing with local customer portal
  return `http://localhost:5174/${slug}${pathSuffix}`;
}
